import { useState } from 'react';

export default function ResearchCTA() {
  const [formData, setFormData] = useState({ name: '', email: '', role: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d7c4ot8fgr5j5eoaving', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact-researchers" className="py-20 bg-gray-50">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">

        {/* Top callout banner */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-500 to-accent-600 p-10 lg:p-14 mb-14 text-center">
          <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/10 pointer-events-none" />
          <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full bg-white/10 pointer-events-none" />

          <div className="relative z-10">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-5">
              <i className="ri-microscope-line text-white text-2xl"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
              We are seeking researchers and clinicians.
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto font-body leading-relaxed mb-8">
              If you are a geneticist, cardiologist, pediatrician, or researcher with an interest in CSCF or MAP3K7-related conditions, we would love to connect. Your expertise could make a real difference for families navigating this diagnosis.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#contact-form"
                className="px-8 py-4 bg-white text-brand-600 font-bold rounded-full hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer font-body flex items-center gap-2"
              >
                <i className="ri-mail-send-line"></i>
                Contact Us
              </a>
              <a
                href="/science"
                className="px-8 py-4 bg-white/15 border-2 border-white/40 text-white font-semibold rounded-full hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer font-body flex items-center gap-2"
              >
                <i className="ri-file-paper-2-line"></i>
                View Published Cases
              </a>
            </div>
          </div>
        </div>

        {/* Contact form + info side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left: Who we want to hear from */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900 font-heading">Who We Want to Hear From</h3>
            <p className="text-gray-500 text-sm font-body leading-relaxed">
              CSCF Support is a family-led organization. We are not a research institution — but we are deeply committed to connecting families with the researchers and clinicians who can help advance understanding of this condition.
            </p>

            <div className="space-y-4">
              {[
                { icon: 'ri-test-tube-line', title: 'Geneticists & Genetic Counselors', text: 'Especially those with experience in MAP3K7-related conditions or rare skeletal dysplasias.' },
                { icon: 'ri-heart-pulse-line', title: 'Pediatric Cardiologists', text: 'With experience in complex congenital heart disease and rare genetic syndromes.' },
                { icon: 'ri-stethoscope-line', title: 'Pediatricians & Specialists', text: 'Any clinician who has encountered or suspects a CSCF diagnosis in a patient.' },
                { icon: 'ri-microscope-line', title: 'Researchers', text: 'Those interested in TAK1 signaling, rare skeletal conditions, or ultra-rare genetic syndromes.' },
                { icon: 'ri-group-line', title: 'Patient Advocates', text: 'Individuals or organizations working in the rare disease space who want to collaborate.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-4 bg-white border border-gray-100 rounded-xl">
                  <div className="w-9 h-9 flex items-center justify-center bg-brand-50 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-brand-500 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 font-heading">{item.title}</p>
                    <p className="text-xs text-gray-500 font-body leading-relaxed mt-0.5">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-brand-50 border border-brand-100 rounded-2xl p-5">
              <p className="text-sm text-brand-700 font-body leading-relaxed">
                <strong className="font-heading">Direct contact:</strong> You can also reach us at{' '}
                <span className="font-semibold">hello@cscfsupport.org</span>
              </p>
            </div>
          </div>

          {/* Right: Contact form */}
          <div id="contact-form" className="bg-white rounded-2xl border border-gray-100 p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-brand-50 rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-brand-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 font-heading">Message Received!</h3>
                <p className="text-gray-500 font-body text-sm leading-relaxed">
                  Thank you for reaching out. We will be in touch as soon as possible.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading">Contact Us</h3>
                <p className="text-gray-400 text-sm mb-7 font-body">
                  Researchers, clinicians, and advocates — we would love to hear from you.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Dr. Jane Smith"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors font-body"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@institution.edu"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors font-body"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">I am a...</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors cursor-pointer font-body"
                    >
                      <option value="">Select your role</option>
                      <option value="geneticist">Geneticist / Genetic Counselor</option>
                      <option value="cardiologist">Pediatric Cardiologist</option>
                      <option value="pediatrician">Pediatrician / Specialist</option>
                      <option value="researcher">Researcher / Scientist</option>
                      <option value="advocate">Patient Advocate / Organization</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your interest in CSCF, your research, or how you would like to collaborate..."
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors resize-none font-body"
                    />
                    <p className={`text-xs mt-1 text-right font-body ${formData.message.length >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
                      {formData.message.length}/500
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || formData.message.length > 500}
                    className="w-full py-3.5 bg-brand-500 text-white font-semibold rounded-full hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 font-body"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
